import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {MapPin, Briefcase, MessageSquare, ShoppingBag, User, X, ChevronRight, Clock} from 'lucide-react';
import { lumi } from '../lib/lumi';
import LoginModal from './LoginModal';

const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showStoreDrawer, setShowStoreDrawer] = useState(false);
  const [stores, setStores] = useState<any[]>([]);
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh';
  const user = lumi.auth.user;

  // 預設分店資料
  const defaultStores = [
    {
      _id: "xinyi-flagship",
      name: "ONGU Studio 信義店",
      nameEn: "ONGU Studio Xinyi Store",
      slug: "xinyi",
      address: "110臺北市信義區信義路四段389號1樓",
      hours: "週一至週五 08:00-20:00",
      phone: "02-2345-6789",
      image: "https://static.lumi.new/43/436fdf374d21ad86da042379ba0813b9.webp"
    }
  ];

  // 載入分店資料
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await lumi.entities.stores.list({
          filter: { status: "active" },
          sort: { order: 1 }
        });
        setStores(response.list?.length > 0 ? response.list : defaultStores);
      } catch (error) {
        console.error("Failed to fetch stores:", error);
        setStores(defaultStores);
      }
    };

    fetchStores();
  }, []);

  const content = {
    zh: {
      store: '門市查詢',
      wholesale: '商用專區',
      contact: '聯繫專員',
      shop: '線上購物',
      member: '登入會員',
      logout: '會員中心',
      storeDrawerTitle: '選擇門市',
      businessHours: '營業時間',
      viewDetails: '查看詳情'
    },
    en: {
      store: 'Stores',
      wholesale: 'B2B',
      contact: 'Contact',
      shop: 'Shop',
      member: 'Sign In',
      logout: 'Member',
      storeDrawerTitle: 'Select Store',
      businessHours: 'Hours',
      viewDetails: 'View Details'
    }
  };

  const t = content[currentLang];



  const handleMemberClick = () => {
    if (user) {
      navigate(`/${currentLang}/member`);
    } else {
      setShowLoginModal(true);
    }
  };

  return (
    <>
      {/* 底部固定导航栏 - 仅在移动端显示 */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 z-50 safe-area-pb">
        <div className="grid grid-cols-5 h-16">
          {/* 门市查询 */}
          <button
            onClick={() => setShowStoreDrawer(true)}
            className="flex flex-col items-center justify-center space-y-1 hover:bg-gray-700 transition-colors duration-200"
          >
            <MapPin size={20} className="text-gray-300" />
            <span className="text-[10px] text-gray-300 font-light">{t.store}</span>
          </button>

          {/* 商用專區 */}
          <Link
            to={`/${currentLang}/wholesale`}
            className="flex flex-col items-center justify-center space-y-1 hover:bg-gray-700 transition-colors duration-200"
          >
            <Briefcase size={20} className="text-gray-300" />
            <span className="text-[10px] text-gray-300 font-light">{t.wholesale}</span>
          </Link>

          {/* 聯繫專員 */}
          <a
            href="https://line.me/R/ti/p/@993lrijj"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center space-y-1 hover:bg-gray-700 transition-colors duration-200"
          >
            <MessageSquare size={20} className="text-gray-300" />
            <span className="text-[10px] text-gray-300 font-light">{t.contact}</span>
          </a>

          {/* 线上购物 */}
          <Link
            to={`/${currentLang}/shop`}
            className="flex flex-col items-center justify-center space-y-1 hover:bg-gray-700 transition-colors duration-200"
          >
            <ShoppingBag size={20} className="text-gray-300" />
            <span className="text-[10px] text-gray-300 font-light">{t.shop}</span>
          </Link>

          {/* 登入会员 */}
          <button
            onClick={handleMemberClick}
            className="flex flex-col items-center justify-center space-y-1 hover:bg-gray-700 transition-colors duration-200"
          >
            <User size={20} className="text-gray-300" />
            <span className="text-[10px] text-gray-300 font-light">
              {user ? t.logout : t.member}
            </span>
          </button>
        </div>
      </div>

      {/* 为底部导航栏预留空间 - 仅在移动端 */}
      <div className="lg:hidden h-16" />

      {/* 门市选择抽屉 */}
      {showStoreDrawer && (
        <>
          {/* 遮罩层 */}
          <div 
            className="lg:hidden fixed inset-0 bg-black/60 z-[60] transition-opacity duration-300"
            onClick={() => setShowStoreDrawer(false)}
          />
          
          {/* 底部抽屉 */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 z-[70] rounded-t-2xl shadow-2xl animate-slide-up max-h-[70vh] overflow-hidden">
            {/* 标题栏 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-light tracking-wider text-gray-900 dark:text-white">
                {t.storeDrawerTitle}
              </h3>
              <button
                onClick={() => setShowStoreDrawer(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            
            {/* 分店列表 */}
            <div className="overflow-y-auto max-h-[calc(70vh-65px)]">
              {stores.map((store) => (
                <button
                  key={store._id}
                  onClick={() => {
                    setShowStoreDrawer(false);
                    navigate(`/${currentLang}/store/${store.slug}`);
                  }}
                  className="w-full px-6 py-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div className="flex items-start space-x-4">
                    {/* 分店图片 */}
                    {store.image && (
                      <img
                        src={store.image}
                        alt={currentLang === 'en' ? (store.nameEn || store.name) : store.name}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    
                    {/* 分店信息 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-base font-light text-gray-900 dark:text-white tracking-wide">
                          {currentLang === 'en' ? (store.nameEn || store.name) : store.name}
                        </h4>
                        <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
                      </div>
                      
                      <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-start">
                          <MapPin size={12} className="mr-1.5 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{store.address}</span>
                        </div>
                        {store.hours && (
                          <div className="flex items-center">
                            <Clock size={12} className="mr-1.5 flex-shrink-0" />
                            <span>{store.hours}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 登录弹窗 */}
      {showLoginModal && (
        <LoginModal 
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLogin={() => {
            lumi.auth.signIn();
            setShowLoginModal(false);
          }}
          currentLang={currentLang}
        />
      )}
    </>
  );
};

export default MobileBottomNav;
